import { useEffect, useState, useMemo, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, Share2, Twitter, Facebook, Linkedin, Link2, Sparkles, Loader2 } from "lucide-react";
import { api, formatDate, categorySlug, formatApiError } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import Lightbox from "@/components/Lightbox";
import Seo, { absoluteUrl, SITE_NAME } from "@/components/Seo";
import { InlineMarkdown } from "@/components/InlineMarkdown";
import AdSlot from "@/components/AdSlot";
import { AD_SLOTS } from "@/lib/adSlots";
import { toast } from "sonner";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Build a unified gallery: cover + every inline image, deduped, in body order.
  // Each entry is { url, caption } so the lightbox can render the caption too.
  const gallery = useMemo(() => {
    if (!article) return [];
    const out = [];
    const seen = new Set();
    const push = (url, caption) => {
      if (url && !seen.has(url)) {
        seen.add(url);
        out.push({ url, caption: caption || "" });
      }
    };
    push(article.cover_image, article.cover_caption);
    const imgRe = /!\[([^\]]*)\]\(([^)]+)\)/g;
    let m;
    while ((m = imgRe.exec(article.body || "")) !== null) push(m[2], m[1]);
    return out;
  }, [article]);

  const openAt = (url) => {
    const i = gallery.findIndex((g) => g.url === url);
    setLightboxIndex(i >= 0 ? i : 0);
  };

  useEffect(() => {
    setLoading(true);
    setSummary(null);
    setArticle(null);
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get(`/articles/${slug}`).then(({ data }) => {
      setArticle(data);
      setSummary(data.ai_summary);
      // fetch related by category
      api.get(`/articles?category=${categorySlug(data.category)}&limit=8`).then((r) => {
        setRelated(r.data.filter((a) => a.slug !== data.slug).slice(0, 4));
      }).catch(() => {});
    }).catch(() => setArticle(false)).finally(() => setLoading(false));
  }, [slug]);

  const generateSummary = async () => {
    if (!article) return;
    setGeneratingSummary(true);
    try {
      const { data } = await api.post(`/articles/${article.slug}/summary`);
      setSummary(data.summary);
      if (!data.cached) toast.success("AI summary ready.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate summary.");
    } finally {
      setGeneratingSummary(false);
    }
  };

  // The URL we share to social platforms is the backend OG stub, NOT the
  // React /article/{slug} URL. Reason: Facebook / Twitter / LinkedIn don't
  // run JavaScript when scraping, so the React Helmet OG tags never reach
  // them on the static build — they only see the generic index.html fallback.
  // The /api/og/article/{slug} endpoint returns a server-rendered HTML stub
  // with article-specific og:image, og:title, og:description, plus a meta
  // refresh that bounces real human visitors straight to /article/{slug}.
  const origin = typeof window !== "undefined" ? window.location.origin : "https://hintononline.com";
  const articlePathUrl = typeof window !== "undefined" ? window.location.href : "";
  const ogShareUrl = article ? `${origin}/api/og/article/${article.slug}` : articlePathUrl;
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(ogShareUrl)}&text=${encodeURIComponent(article?.title || "")}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogShareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(ogShareUrl)}`,
  };
  const copyLink = async () => {
    // Copy the OG-friendly URL so even when users paste links into messaging
    // apps (WhatsApp / Slack / Discord) the preview shows the article image.
    try { await navigator.clipboard.writeText(ogShareUrl); toast.success("Link copied."); } catch (err) { console.warn("clipboard copy failed:", err); }
  };

  if (loading) return (
    <>
      <Seo title="Loading…" noindex />
      <div className="px-6 py-32 text-center font-serif-ed italic text-2xl text-neutral-500">Loading…</div>
    </>
  );
  if (!article) return (
    <>
      <Seo title="Story not found" noindex />
      <div className="px-6 py-32 text-center">
        <h1 className="font-serif-ed italic text-4xl">Story not found.</h1>
        <Link to="/" className="mt-6 inline-block link-expand text-sm uppercase tracking-widest">Back home</Link>
      </div>
    </>
  );

  const paragraphs = (article.body || "").split(/\n\n+/);

  // Inject one in-article ad after ~40% of the body, only on articles long
  // enough that the ad isn't dropped immediately under the headline.
  const midAdIndex = paragraphs.length >= 8 ? Math.floor(paragraphs.length * 0.4) : -1;

  // Render a single paragraph block (image / heading / quote / list / text).
  // Extracted so it can be called inside the .map() while also allowing
  // the in-article ad to be injected between paragraphs.
  function renderParagraph(p, i, total) {
    // YouTube embed:  "!youtube[caption](VIDEO_ID)" on its own paragraph line.
    const ym = p.match(/^!youtube\[(.*?)\]\(([A-Za-z0-9_-]{11})\)$/);
    if (ym) {
      const key = `${i}-yt-${ym[2]}`;
      return (
        <figure key={key} className="my-10 -mx-1" data-testid={`article-body-youtube-${i}`}>
          <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${ym[2]}`}
              title={ym[1] || "YouTube video"}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              loading="lazy"
            />
          </div>
          {ym[1] && <figcaption className="mt-3 text-xs font-mono-ed uppercase tracking-[0.18em] text-neutral-500">{ym[1]}</figcaption>}
        </figure>
      );
    }
    // Video embed:  "!video[caption](url)" on its own paragraph line.
    // Kept as a separate pattern so it never collides with markdown images.
    const vm = p.match(/^!video\[(.*?)\]\((.+?)\)$/);
    if (vm) {
      const key = `${i}-video-${(vm[2] || "").slice(0, 24)}`;
      return (
        <figure key={key} className="my-10 -mx-1" data-testid={`article-body-video-${i}`}>
          <video
            src={vm[2]}
            controls
            playsInline
            preload="metadata"
            className="block w-full h-auto bg-black"
          >
            Sorry, your browser can&rsquo;t play this video.
          </video>
          {vm[1] && <figcaption className="mt-3 text-xs font-mono-ed uppercase tracking-[0.18em] text-neutral-500">{vm[1]}</figcaption>}
        </figure>
      );
    }
    const m = p.match(/^!\[(.*?)\]\((.+?)\)$/);
    const key = `${i}-${(p || "").slice(0, 32)}`;
    let body;
    if (m) {
      body = (
        <figure className="my-10 -mx-1">
          <img
            src={m[2]}
            alt={m[1]}
            onClick={() => openAt(m[2])}
            className="block w-full h-auto cursor-zoom-in transition-opacity hover:opacity-95"
            data-testid={`article-body-image-${i}`}
          />
          {m[1] && <figcaption className="mt-3 text-xs font-mono-ed uppercase tracking-[0.18em] text-neutral-500">{m[1]}</figcaption>}
        </figure>
      );
    } else {
      // Strip optional alignment prefix from the paragraph body.
      // Syntax: paragraph begins with "::center::" or "::right::".
      let block = p;
      let align = "left";
      const alignMatch = block.match(/^::(center|right)::\s*/);
      if (alignMatch) {
        align = alignMatch[1];
        block = block.slice(alignMatch[0].length);
      }
      const alignCls = align === "center" ? "text-center" : align === "right" ? "text-right" : "";

      if (block.startsWith("## ")) {
        body = (
          <h2 className={`font-serif-ed font-semibold text-2xl md:text-3xl mt-10 mb-4 leading-tight ${alignCls}`}>
            <InlineMarkdown text={block.slice(3)} />
          </h2>
        );
      } else if (block.split("\n").every((l) => l.startsWith("> "))) {
        const quoted = block.split("\n").map((l) => l.slice(2)).join(" ");
        body = (
          <blockquote className={`my-8 border-l-4 border-[var(--hinton-red)] pl-5 italic font-serif-ed text-xl md:text-2xl text-neutral-700 ${alignCls}`}>
            <InlineMarkdown text={quoted} />
          </blockquote>
        );
      } else {
        const lines = block.split("\n");
        if (lines.length > 0 && lines.every((l) => /^- /.test(l))) {
          body = (
            <ul className={`list-disc list-outside pl-6 my-5 space-y-2 ${alignCls}`}>
              {lines.map((l, idx) => (
                <li key={idx}><InlineMarkdown text={l.slice(2)} /></li>
              ))}
            </ul>
          );
        } else if (lines.length > 0 && lines.every((l) => /^\d+\.\s/.test(l))) {
          body = (
            <ol className={`list-decimal list-outside pl-6 my-5 space-y-2 ${alignCls}`}>
              {lines.map((l, idx) => (
                <li key={idx}><InlineMarkdown text={l.replace(/^\d+\.\s/, "")} /></li>
              ))}
            </ol>
          );
        } else {
          body = <p className={alignCls}><InlineMarkdown text={block} /></p>;
        }
      }
    }
    // After the chosen mid-paragraph, drop in the in-article ad. Only renders
    // when the slot ID has been set in /app/frontend/src/lib/adSlots.js so the
    // layout doesn't pick up empty spacing pre-approval. Wrapped in
    // `not-prose` so prose-hinton typography doesn't restyle it.
    const showInBodyAd = i === midAdIndex && i < total - 1 && !!AD_SLOTS.articleInBody?.slot;
    const tail = showInBodyAd ? (
      <div className="not-prose my-10" data-testid="article-inbody-ad-wrapper">
        <AdSlot id="articleInBody" label={false} />
      </div>
    ) : null;
    return (
      <Fragment key={key}>
        {body}
        {tail}
      </Fragment>
    );
  }

  // SEO description — prefer the editorial excerpt, fall back to a clean prefix of the body.
  const seoDescription = (article.excerpt || article.body || "")
    .replace(/!\[[^\]]*\]\([^)]+\)/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);

  const canonicalPath = `/article/${article.slug}`;
  const publishedIso = article.original_published_at || article.created_at;

  // JSON-LD NewsArticle schema for Google News & rich snippets
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": absoluteUrl(canonicalPath),
    },
    "headline": article.title,
    "description": seoDescription,
    "image": article.cover_image ? [article.cover_image] : [],
    "datePublished": publishedIso,
    "dateModified": article.updated_at || publishedIso,
    "author": [
      {
        "@type": "Person",
        "name": article.author || "Hinton Editorial",
        ...(article.author_slug
          ? { "url": absoluteUrl(`/author/${article.author_slug}`) }
          : {}),
      },
    ],
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": absoluteUrl("/logo512.png"),
      },
    },
    "articleSection": article.category,
    ...(Array.isArray(article.categories) && article.categories.length > 0
      ? { "keywords": article.categories.join(", ") }
      : {}),
  };

  return (
    <article data-testid="article-page">
      <Seo
        title={article.title}
        description={seoDescription}
        canonical={canonicalPath}
        image={article.cover_image}
        type="article"
        author={article.author}
        publishedAt={publishedIso}
        updatedAt={article.updated_at}
        section={article.category}
        tags={article.categories || []}
        jsonLd={articleLd}
      />
      {/* Hero */}
      <header className="px-4 sm:px-6 lg:px-10 pt-12 md:pt-16 pb-10 border-b border-black/15">
        <div className="max-w-4xl mx-auto">
          <Link to={`/category/${categorySlug(article.category)}`} className="overline text-[var(--hinton-red)] hover:underline" data-testid="article-category">
            {article.category}
          </Link>
          <h1 className="mt-5 font-serif-ed text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight" data-testid="article-title">
            {article.title}
          </h1>
          {article.excerpt && (
            <p className="mt-6 text-xl md:text-2xl font-serif-ed italic text-neutral-700 leading-relaxed max-w-3xl">
              {article.excerpt}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4 text-[11px] font-mono-ed uppercase tracking-[0.22em] text-neutral-500">
            {article.author && article.author !== "Hinton Editorial" && (
              <>
                <span className="flex items-center gap-2">
                  {article.author_thumbnail && (
                    <img src={article.author_thumbnail} alt="" className="w-7 h-7 object-cover border border-black/10" />
                  )}
                  By {article.author_slug ? (
                    <Link to={`/author/${article.author_slug}`} className="hover:text-[var(--hinton-red)] underline-offset-4 hover:underline" data-testid="article-author-link">
                      {article.author}
                    </Link>
                  ) : article.author}
                </span>
                <span>·</span>
              </>
            )}
            <span data-testid="article-published">{formatDate(article.original_published_at || article.created_at)}</span>
            {article.updated_at && (
              <>
                <span>·</span>
                <span data-testid="article-updated">Updated {formatDate(article.updated_at)}</span>
              </>
            )}
            <span>·</span>
            <span>{Math.max(1, Math.ceil((article.body?.length || 0) / 1200))} min read</span>
          </div>
        </div>
      </header>

      <div className="w-full max-h-[88vh] overflow-hidden border-b border-black/10 bg-neutral-100">
        <img
          src={article.cover_image}
          alt={article.title}
          onClick={() => openAt(article.cover_image)}
          className="block w-full h-auto max-h-[88vh] object-contain mx-auto cursor-zoom-in transition-opacity hover:opacity-95"
          data-testid="article-cover-image"
        />
      </div>
      {article.cover_caption && (
        <div className="px-4 sm:px-6 lg:px-10 pt-4">
          <p
            className="max-w-4xl mx-auto text-xs font-mono-ed uppercase tracking-[0.18em] text-neutral-500"
            data-testid="article-cover-caption"
          >
            {article.cover_caption}
          </p>
        </div>
      )}

      {/* Body */}
      <div className="px-4 sm:px-6 lg:px-10 py-12 md:py-20">
        <div className="relative max-w-[1400px] mx-auto">
          {/* LEFT skyscraper rail — desktop only.
              Hidden until xl (1280px+) so we never crush the article column. */}
          <aside
            aria-hidden="true"
            className="hidden xl:block absolute left-0 top-0 w-[160px] h-full pointer-events-none"
          >
            <div className="sticky top-24 pointer-events-auto">
              <AdSlot id="articleSideLeft" />
            </div>
          </aside>
          {/* RIGHT skyscraper rail — desktop only. */}
          <aside
            aria-hidden="true"
            className="hidden xl:block absolute right-0 top-0 w-[160px] h-full pointer-events-none"
          >
            <div className="sticky top-24 pointer-events-auto">
              <AdSlot id="articleSideRight" />
            </div>
          </aside>
          <div className="max-w-3xl mx-auto">
          <div className="prose-hinton" data-testid="article-body">
            {paragraphs.map((p, i) => renderParagraph(p, i, paragraphs.length))}
          </div>

          {/* Share */}
          <div className="mt-14 pt-8 border-t border-black/15 flex flex-wrap items-center gap-3" data-testid="share-bar">
            <span className="overline text-neutral-500 mr-2 flex items-center gap-2"><Share2 size={14} /> Share</span>
            <a href={shareLinks.twitter} target="_blank" rel="noreferrer" aria-label="Share on Twitter" className="p-2 border border-black/15 hover:bg-black hover:text-white transition-colors" data-testid="share-twitter"><Twitter size={16} /></a>
            <a href={shareLinks.facebook} target="_blank" rel="noreferrer" aria-label="Share on Facebook" className="p-2 border border-black/15 hover:bg-black hover:text-white transition-colors" data-testid="share-facebook"><Facebook size={16} /></a>
            <a href={shareLinks.linkedin} target="_blank" rel="noreferrer" aria-label="Share on LinkedIn" className="p-2 border border-black/15 hover:bg-black hover:text-white transition-colors" data-testid="share-linkedin"><Linkedin size={16} /></a>
            <button onClick={copyLink} aria-label="Copy link" className="p-2 border border-black/15 hover:bg-black hover:text-white transition-colors" data-testid="share-copy"><Link2 size={16} /></button>
          </div>
        </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-10 py-14 md:py-20 bg-white border-t border-black/10">
          <div className="flex items-end justify-between border-b border-black/20 pb-4 mb-8">
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-[-0.03em]">Continue Reading</h2>
            <Link to={`/category/${categorySlug(article.category)}`} className="text-xs uppercase tracking-[0.22em] font-semibold hover:text-[var(--hinton-red)] transition-colors">
              More {article.category}
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {related.map((a) => <ArticleCard key={a.id} article={a} size="sm" showExcerpt={false} />)}
          </div>
        </section>
      )}

      {lightboxIndex !== null && (
        <Lightbox
          images={gallery}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </article>
  );
}
